/**
 * Per-country detail for the public coverage pages (/coverage and /coverage/<code>). ENGLISH ONLY at launch.
 *
 * Every sentence here is copied in plain words from code and its release notes — never invented:
 *   - tier + member sentence: aicio/validation_status.py `_LAUNCH` / `launch_note()` (BLOCKER_GROUP_PHRASES)
 *   - "still being verified": aicio/validation_status.py `_STATUS[..].suspected`
 *   - "MyCIO will refuse to give a figure when…": docs/RELEASE_TAX_COUNTRIES.md "Launch-standard disposition"
 *     table + the refusals named in `_STATUS[..].suspected`
 *   - what is encoded: src/lib/mycio/marketingCoverage.ts `jurisdictionCoverage` (edition, law, rules, limits)
 *   - cross-border: aicio/validation_status.py `_COMBINATIONS` (US-UK) + the pair-containment rule
 *   - declared engine coverage (wrappers deliberately not in the menu, market-wide MC gaps): aicio/
 *     engine_coverage.py (C-COV), via src/lib/mycio/engineCoverageNotes.generated.ts (scripts/
 *     generate_engine_coverage_notes.py) — merged into `verifying` below, never hand-duplicated
 *   - survivor-income refusal (every market alike, `SURVIVOR_REFUSAL` below): aicio/mc_assessment_
 *     capability.py:1440-1458 (MCAssessmentUnavailable, unconditional — no AICIO_SECOND_LIFE gate on
 *     it) — merged into every country's `refuses` in `countryDetails()`, not an engine_coverage row
 *     (a whole-computation refusal, not a `disclose`-shaped partial gap; see that module's docstring)
 *   - partner-income refusal (every market alike, `PARTNER_INCOME_REFUSAL` below): aicio/mc_assessment_
 *     capability.py:1401-1439 — narrowed 2026-09-29, then narrowed again 2026-09-29 (founder review),
 *     wording corrected 2026-09-29 (founder review R2-3): a partner-owned pension/annuity/benefit
 *     stream (`evaluate._NONEARNED_KINDS`, minus labour income) with no stated survivor continuation
 *     now prices on the partner's own return (or the confirmed joint return) WHEN the partner's age is
 *     stated — computes, is NOT refused, and must not read as refused. `partner_earned` pay stamped
 *     tax_unit="own" (`partner_income.streams()`, UK/CA/IT/NL/ES/PT) ALSO already prices on the
 *     partner's own return and is NOT refused (the `PARTNER_EARNED_KIND and unit == "own"` continue,
 *     mc_assessment_capability.py:1417). What IS STILL refused, and belongs in the public text: (1) a
 *     partner stream that would still be paying a household survivor after the partner's own death
 *     (mirroring SURVIVOR_REFUSAL, own-life case); (2) a partner's pay TAXED ON A JOINT RETURN (unit !=
 *     "own"), or pay the partner keeps earning after retiring (`earned_post_retirement`, always
 *     labour/`evaluate._LABOUR_INCOME_KINDS`, never priced on the partner's own return here); (3) a
 *     partner's rental income or any other stream kind outside the priced set (never reaches the
 *     own-return route at all); (4) any partner income when the partner's age is not stated
 *     (`MCPartnerAgeRequiredAssessmentUnavailable` — the member's own fix is to state it, which this
 *     page does not need to say since the member sees that guidance directly in-product). Merged into
 *     every country's `refuses` the same way as `SURVIVOR_REFUSAL`. Wording consistently says "your
 *     partner" throughout (was switching to "the household's partner" mid-sentence).
 *   - US SS / CA OAS post-death approximation (added to `verifying` under US/CA, 2026-09-29, founder
 *     review R4): once a survivor's own-return pay/OAS/SS is pooled onto the household's shared book
 *     after the primary's death (`evaluate._route`'s `force_pool`, founder review R3-2), the widened
 *     base still excludes per-path portfolio-withdrawal amounts -- `_annuity_year_arrays` builds a
 *     deterministic per-year tensor before the Monte Carlo simulation runs and structurally cannot see
 *     a live, per-path withdrawal decision (only the plan's stated baseline ordinary_income is added).
 *     Understates the clawback/inclusion tax when withdrawals are large; disclosed here rather than
 *     only in code comments.
 * Only the US and UK are "validated" (LAUNCH_COVERAGE in landing/content.ts). Re-review with every coverage
 * release; when the registry changes, change this file in the same commit (for the engine_coverage
 * source, regenerate `engineCoverageNotes.generated.ts` instead of hand-editing `DETAIL` below).
 */
import { LAUNCH_COVERAGE, type CoverageTier } from '@/components/mycio/marketing/landing/content'
import { jurisdictionCoverage } from '@/lib/mycio/marketingCoverage'
import { engineCoverageNotesFor } from '@/lib/mycio/engineCoverageNotes.generated'

export type CountryDetail = {
  /** ISO region (US, GB, …) — the LAUNCH_COVERAGE `code`. */
  code: string
  /** URL slug and flagcdn file name (us, gb, …) — the LAUNCH_COVERAGE `flag`. */
  slug: string
  name: string
  tier: CoverageTier
  /** The one member-facing sentence from `launch_note()`; validated countries have none. */
  memberSentence: string | null
  /** Plain-English readings of `_STATUS[code].suspected` — what is modelled approximately or not yet. */
  verifying: string[]
  /** Each item completes "MyCIO will refuse to give a figure when …". */
  refuses: string[]
  /** Cross-border notes that apply to this country (US-UK combination, pair containment). */
  crossBorder: string[]
}

// Plain words (founder, 2026-09-20: the previous sentence — "contained, not promised" — meant nothing to a reader).
const PAIR_RULE = 'Supporting two countries on their own is not the same as supporting a move between them. So far we have only checked the US–UK pair. For any other pair the plan still runs, but it marks the cross-border tax figures as unverified estimates rather than treating them as reliable.'
const US_UK = 'A US citizen living in the UK is taxed by both. US tax on UK funds, on ISAs and on selling a home is still being verified for that combination.'

// Every launch market alike (aicio/mc_assessment_capability.py:1440-1458 — MCAssessmentUnavailable,
// unconditional: no AICIO_SECOND_LIFE gate on it). Appended to every country's `refuses` in
// `countryDetails()` rather than listed ten times below.
const SURVIVOR_REFUSAL = 'the household is married and a stated income stream would still be paying a surviving spouse within the years this plan projects.'

// Every launch market alike (aicio/mc_assessment_capability.py:1401-1439, narrowed 2026-09-29, then
// again 2026-09-29 — founder review M6, wording corrected 2026-09-29 — founder review R2-3). A
// partner-owned pension/annuity/benefit stream with no stated survivor continuation is priced on the
// partner's own return (or the confirmed joint return) once the partner's age is stated -- computes,
// not refused. Partner PAY on the partner's own return (`partner_earned`, tax_unit="own") ALSO already
// computes and is not refused. STILL refused: a stream that would keep paying a household survivor
// after the partner's own death (mirroring SURVIVOR_REFUSAL); the partner's pay taxed on a JOINT
// return, or pay the partner keeps earning after retiring; a partner's rental or other income kind
// outside the priced set; and any partner income at all when the partner's age is not stated.
// Consistently "your partner" (was switching to "the household's partner" mid-sentence). Appended to
// every country's `refuses` in `countryDetails()`, keyed the same way as SURVIVOR_REFUSAL.
const PARTNER_INCOME_REFUSAL = "your partner has a stated taxable income stream that would still be paying a surviving member of the household after your partner's own death, your partner's pay is taxed on a joint return, your partner keeps earning pay after retiring, your partner has rental or other income outside what this plan can yet price on your partner's own return, or your partner's income is stated but your partner's age is not, within the years this plan projects."

const DETAIL: Record<string, Omit<CountryDetail, 'code' | 'slug' | 'name' | 'tier'>> = {
  US: {
    memberSentence: null,
    verifying: [
      'After a spouse’s death, Social Security tax for the surviving partner does not yet see the household’s other investment withdrawals, which can understate that tax.',
    ],
    refuses: [],
    crossBorder: [US_UK, PAIR_RULE],
  },
  GB: {
    memberSentence: null,
    verifying: [
      'From 6 April 2027 unused pensions will count in the inheritance-tax estate. MyCIO models the rules before that date.',
    ],
    refuses: [],
    crossBorder: [US_UK, PAIR_RULE],
  },
  CA: {
    memberSentence: 'Tax figures for Canada are provisional: tax in the year of death is still being verified.',
    verifying: [
      'Low-income tax reductions outside Ontario and British Columbia are not modelled.',
      'Quebec’s age and retirement amounts are not modelled.',
      'In the year of death, only the deemed capital gain and RRSP/RRIF income are added to the final return; other death-year income is not.',
      'Combined employee and self-employed contribution ceilings and elections are not modelled.',
      'After a spouse’s death, Old Age Security clawback for the surviving partner does not yet see the household’s other investment withdrawals, which can understate the clawback.',
    ],
    refuses: [
      'the plan keeps an asset that may have gone up in value until death, because the tax on that final return is not yet priced.',
    ],
    crossBorder: [PAIR_RULE],
  },
  DE: {
    memberSentence: 'Tax figures for Germany are provisional: joint taxation of couples and church tax are still being verified.',
    verifying: [
      'Church tax (8–9% of income tax) is not modelled.',
      'A jointly assessed two-earner couple is priced per earner by the household projection; the main projection does not yet use per-owner wages.',
      'Child allowances (Kinderfreibetrag versus Kindergeld) are not modelled.',
    ],
    refuses: [
      'you pay church tax.',
      'a child in the plan might qualify for the child-allowance versus child-benefit comparison, which is not modelled.',
      'accounts are owned by one person and returns are individual, the couple’s assessment unit is unknown, or a death could change a joint assessment.',
    ],
    crossBorder: [PAIR_RULE],
  },
  FR: {
    memberSentence: 'Tax figures for France are provisional: prior gifts and succession allowances are still being verified.',
    verifying: [
      'The IFI cap (wealth tax plus income tax limited to 75% of income) is not modelled.',
      'Life-insurance premiums paid after age 70 (article 757 B) are still being verified.',
      'Extra half-parts for widowed, disabled or veteran taxpayers, and the CEHR/CDHR surtaxes, are not modelled.',
      'Shared custody, disability and attaching an adult child to the household need more family facts than the plan holds.',
    ],
    refuses: [
      'the plan states gifts already made in the past, because the succession allowances for them are not yet verified.',
    ],
    crossBorder: [PAIR_RULE],
  },
  IT: {
    memberSentence: 'Tax figures for Italy are provisional: the pension tax credit is still being verified.',
    verifying: [
      'Regional surtaxes use all 2026 regional scales; municipal surtaxes are encoded for 7 comuni and the national average is used elsewhere.',
      'The 12.5% rate on state bonds applies only to the share of interest you say comes from them.',
    ],
    refuses: [
      'the plan has a regular pension taxed as ordinary income, because the pensioner tax credit does not yet stay attached to that income.',
    ],
    crossBorder: [PAIR_RULE],
  },
  ES: {
    memberSentence: 'Tax figures for Spain are provisional: tax on selling property is still being verified.',
    verifying: [
      'The 2026 regional scales and personal minimums are assumed unchanged from the 2025 AEAT manual; the Illes Balears over-65 minimum is still to be confirmed.',
      'Family minimums, joint filing and regional deductions are not modelled.',
      'Treaty caps for Canada, France, Italy, the Netherlands, Portugal and Switzerland come from the 2018 ministry table.',
      'The Basque and Navarra foral systems are disclosure-only: they are named, not computed.',
    ],
    refuses: [
      'a home held in the plan is sold and the over-65 or reinvestment exemption would apply.',
      'the plan has a regular pension, because the pension work deductions do not yet stay attached to that income.',
    ],
    crossBorder: [PAIR_RULE],
  },
  PT: {
    memberSentence: 'Tax figures for Portugal are provisional: tax on long-held investments is still being verified.',
    verifying: [
      'The option to aggregate dividends, interest and gains with other income (englobamento) is not modelled.',
      'Short-hold aggregation and the holding-period ladder need a stated holding period; plans use the flat 28% rate.',
      'A home sold inside a plan is taxed on the 28% gains channel, without the 50% inclusion or the reinvestment relief.',
    ],
    refuses: [
      'the plan sells a property, or depends on how long an investment was held, and the rule for that case is not yet encoded.',
    ],
    crossBorder: [PAIR_RULE],
  },
  NL: {
    memberSentence: 'Tax figures for the Netherlands are provisional: some tax rules are still being verified.',
    verifying: [
      'Box 3 debt, pension exclusions, actual-return relief and deferred exit treatment still need simulation validation.',
    ],
    refuses: [
      'the plan leaves the Netherlands (a residence exit) in a way that is not yet supported.',
    ],
    crossBorder: [PAIR_RULE],
  },
  CH: {
    memberSentence: 'Tax figures for Switzerland are provisional: tax on pension capital withdrawals and church tax are still being verified.',
    verifying: [
      'Cantons other than Zürich apply the Zürich income tariff at a scaled multiplier — an approximation.',
      'Church tax and personal deductions are not modelled; the inputs are taxable income.',
      // NO "taxed on the income tariff, not the capital tariff" bullet here (removed 2026-09-29,
      // C-COV reviewer round 2): a REAL plan with a funded pillar 3a account never reaches that
      // mistaxed path — validate_pension_capital_assessment (aicio/mc_assessment_capability.py:
      // 458-502) refuses it first (MCPensionCapitalAssessmentUnavailable). The `refuses` line below
      // ('the plan withdraws pillar 3a capital.') already states the true, live behaviour; see
      // aicio/engine_coverage.py's module docstring for the two-round trace that found this.
    ],
    refuses: [
      'the plan withdraws pillar 3a capital.',
      'you pay church tax.',
      'your canton is missing, or its compiled rule borrows Zürich’s tariff shape instead of its own schedule.',
    ],
    crossBorder: [PAIR_RULE],
  },
}

export function countryDetails(): CountryDetail[] {
  return LAUNCH_COVERAGE.map((c) => {
    const detail = DETAIL[c.code]
    // C-COV: append the engine's own declared-coverage bullets (aicio/engine_coverage.py, generated
    // via scripts/generate_engine_coverage_notes.py) after the hand-authored ones, deduped — a launch
    // decision (CH Pillar 2, a wrapper NOT_IN_MENU) must show up here the same day it lands in the
    // registry, not wait for a hand edit to this file.
    const engineNotes = engineCoverageNotesFor(c.code).filter((n) => !detail.verifying.includes(n))
    // Survivor income and partner income (every market alike, SURVIVOR_REFUSAL / PARTNER_INCOME_REFUSAL
    // above) are typed refusals, not engine_coverage rows — appended here rather than listed by hand in
    // every country's `refuses`.
    const sharedRefusals = [SURVIVOR_REFUSAL, PARTNER_INCOME_REFUSAL]
      .filter((r) => !detail.refuses.includes(r))
    const refuses = [...detail.refuses, ...sharedRefusals]
    return { code: c.code, slug: c.flag, name: c.name, tier: c.tier, ...detail,
            verifying: [...detail.verifying, ...engineNotes], refuses }
  })
}

export function countryDetail(slug: string): CountryDetail | null {
  return countryDetails().find((c) => c.slug === slug.toLowerCase()) ?? null
}

/** The editorial scope row (edition / law / rules / limits) for a country, by its English name. */
export function encodedScope(name: string) {
  return jurisdictionCoverage.find((j) => j.name === name) ?? null
}

/** Localized country name via Intl.DisplayNames; falls back to the English name (SSR-safe). */
export function localizedCountryName(code: string, language: string | null | undefined, fallback: string): string {
  try {
    const dn = new Intl.DisplayNames([language || 'en'], { type: 'region' })
    return dn.of(code) || fallback
  } catch {
    return fallback
  }
}
